import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { goldCoinTier } from "../../shared/bounty.ts";
import { isPurseSpend, purseAmountLabel, purseLabel, purseNote } from "../../shared/purse.ts";
import type { PurseRow } from "../../shared/types";
import { Empty, ErrorBox } from "./Shell";
import { BusyLabel } from "./BusyLabel";

/**
 * The purse (docs/roadmap-2026-10.md, flag `purseUI`): where the gold went, at the bottom of the
 * Rewards screen. One line per ledger row, newest first, in a bronze card: the label (shared/purse.ts),
 * a muted word when the label alone does not say what happened ("gold rush", "reopened"), then the
 * amount with the board's coins for its size, in gold when earned and muted when spent. "Show more"
 * appends the next page. `refreshKey` (the balance) reloads the first page when the gold changes.
 */
export function PurseSection({ refreshKey }: { refreshKey: unknown }) {
  const [rows, setRows] = useState<PurseRow[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const generation = useRef(0);

  async function loadFirst() {
    const mine = ++generation.current;
    try {
      const page = await api.purse();
      if (mine !== generation.current) return;
      setRows(page.rows);
      setNext(page.next);
      setError(null);
    } catch (e) {
      if (mine === generation.current) setError(e instanceof Error ? e.message : "Could not open the purse");
    }
  }

  useEffect(() => {
    void loadFirst();
  }, [refreshKey]);

  async function loadMore() {
    if (!next || loadingMore) return;
    const mine = generation.current;
    setLoadingMore(true);
    try {
      const page = await api.purse(next);
      if (mine !== generation.current) return;
      setRows((r) => [...(r ?? []), ...page.rows.filter((row) => !(r ?? []).some((x) => x.id === row.id))]);
      setNext(page.next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open the purse");
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section class="purse" aria-labelledby="purse-heading">
      <h2 class="rewards-heading" id="purse-heading">Purse</h2>
      {error && <ErrorBox message={error} onRetry={rows ? undefined : () => void loadFirst()} />}
      {rows && rows.length === 0 && <Empty>Nothing in it yet.</Empty>}
      {rows && rows.length > 0 && (
        <div class="card purse-card">
          <ul class="purse-list">
            {rows.map((row) => (
              <PurseLine key={row.id} row={row} />
            ))}
          </ul>
          {next && (
            <button type="button" class="board-fold purse-more" onClick={() => void loadMore()} disabled={loadingMore}>
              <span class="board-fold-caret" aria-hidden="true">▾</span>
              <BusyLabel busy={loadingMore} label="Loading…">Show more</BusyLabel>
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function PurseLine({ row }: { row: PurseRow }) {
  const label = purseLabel(row);
  const note = purseNote(row);
  const spend = isPurseSpend(row.amount);
  const tier = goldCoinTier(Math.abs(row.amount));
  return (
    <li class={`purse-row${spend ? " purse-spend" : ""}`}>
      <span class="purse-label">{label}</span>
      {note && <span class="purse-note">{note}</span>}
      <span class="purse-amount">
        <span class={`coins coins-${tier} purse-coins`} aria-hidden="true">
          {Array.from({ length: tier }, (_, i) => (
            <img key={i} class="coin" src="/ui/coin@3x.png" width={12} height={12} alt="" />
          ))}
        </span>
        <span class="purse-value">{purseAmountLabel(row.amount)}</span>
        <span class="sr-only"> gold</span>
      </span>
    </li>
  );
}
